import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, ChevronDown, Check, Plus, ListTree } from 'lucide-react';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '../ui/popover';
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
} from '../ui/command';
import { useWorkspace } from '../../context/WorkspaceContext';
import { buildWorkspaceSwitchUrl } from './urlHelpers';
import { cn } from '../../lib/utils';

const SEARCH_THRESHOLD = 5;

/**
 * WorkspacePicker — the [🏢 Workspace Name ▾] segment of the top-bar
 * Context Switcher (Phase 2).
 *
 * - Reads workspaces from WorkspaceContext (Phase 1).
 * - Uses Phase 1's setCurrentWorkspaceId() so the film is cleared
 *   automatically on a real switch.
 * - Click-on-selected is a no-op (closes the popover without navigation).
 */
export default function WorkspacePicker() {
  const navigate = useNavigate();
  const {
    workspaces,
    currentWorkspace,
    currentWorkspaceId,
    setCurrentWorkspaceId,
  } = useWorkspace();
  const [open, setOpen] = useState(false);

  const label =
    currentWorkspace?.name || (currentWorkspaceId ? '…' : 'Select workspace');
  const hasNone = workspaces.length === 0;
  const showSearch = workspaces.length > SEARCH_THRESHOLD;

  // Phase 1 Task 4.3 — single-workspace users get no switcher affordance:
  // render the workspace name as a static label (no popover, no chevron),
  // consistent with Home.jsx's auto-redirect for single-workspace users.
  if (workspaces.length === 1) {
    return (
      <div
        className="inline-flex items-center gap-1.5 max-w-[180px] md:max-w-[220px] h-8 px-2 rounded-md text-sm font-medium text-foreground"
        data-testid="ctx-switcher-workspace-static"
        aria-label="Current workspace"
      >
        <Building2 className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden />
        <span className="truncate">{label}</span>
      </div>
    );
  }

  const handleSelect = (wsId) => {
    setOpen(false);
    if (!wsId) return;
    if (wsId === currentWorkspaceId) return; // no-op on selecting same
    setCurrentWorkspaceId(wsId);
    navigate(buildWorkspaceSwitchUrl(wsId));
  };

  const handleCreate = () => {
    setOpen(false);
    navigate('/workspace/purchase');
  };

  const handleManage = () => {
    setOpen(false);
    navigate('/workspaces');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Select workspace"
          data-testid="ctx-switcher-workspace-trigger"
          data-state={open ? 'open' : 'closed'}
          className={cn(
            'group inline-flex items-center gap-1.5 max-w-[180px] md:max-w-[220px]',
            'h-8 px-2 rounded-md text-sm font-medium',
            'text-foreground hover:bg-muted/60',
            'data-[state=open]:bg-muted',
            'transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1'
          )}
        >
          <Building2 className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden />
          <span className="truncate">{label}</span>
          <ChevronDown
            className={cn(
              'h-3.5 w-3.5 text-muted-foreground shrink-0 opacity-60 transition-transform',
              'group-hover:opacity-100 group-data-[state=open]:rotate-180'
            )}
            aria-hidden
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-72 p-0"
        data-testid="ctx-switcher-workspace-popover"
      >
        <Command>
          {showSearch && (
            <CommandInput placeholder="Search workspaces…" aria-label="Search workspaces" />
          )}
          <CommandList>
            <CommandEmpty>No workspaces found.</CommandEmpty>

            {!hasNone && (
              <CommandGroup heading="Workspaces">
                {workspaces.map((ws) => (
                  <CommandItem
                    key={ws.id}
                    value={`${ws.name} ${ws.id}`}
                    onSelect={() => handleSelect(ws.id)}
                    data-testid={`ctx-switcher-workspace-option-${ws.id}`}
                  >
                    <Building2 className="h-4 w-4 text-muted-foreground" aria-hidden />
                    <span className="truncate flex-1">{ws.name}</span>
                    {ws.id === currentWorkspaceId && (
                      <Check className="h-4 w-4 text-primary" aria-label="current" />
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            <CommandSeparator />
            <CommandGroup>
              <CommandItem
                onSelect={handleCreate}
                data-testid="ctx-switcher-workspace-create"
              >
                <Plus className="h-4 w-4" aria-hidden />
                <span>Create workspace</span>
              </CommandItem>
              {!hasNone && (
                <CommandItem
                  onSelect={handleManage}
                  data-testid="ctx-switcher-workspace-manage"
                >
                  <ListTree className="h-4 w-4" aria-hidden />
                  <span>Manage workspaces</span>
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
